import axiosInstance from "@/api/axios-instance";

export const EVENT_CATEGORIES = [
  "SESSION",
  "ASSESSMENT",
  "DIAGNOSIS",
  "CONSULTATION",
] as const;

export type ParticipantScope = "INDIVIDUAL" | "JOINT";

export interface ManagedEventTeam {
  id: string;
  name: string;
  supported_languages?: string[];
  region_rel?: { code: string; display_name?: string } | null;
  product?: { name: string } | null;
}

export interface ManagedEvent {
  id: string;
  name: string;
  category: string;
  participant_scope: ParticipantScope;
  duration_minutes: number | null;
  round_robin_pool_active: boolean;
  status: "ACTIVE" | "INACTIVE";
  // Free-form descriptive context (terms & conditions, description, etc.).
  metadata?: Record<string, any> | null;
  teams?: ManagedEventTeam[];
  event_types?: Array<{ id: string }>;
  created_at: string;
}

export interface ManagedEventInput {
  team_ids: string[];
  name: string;
  category: string;
  participant_scope?: ParticipantScope;
  duration_minutes?: number | null;
  round_robin_pool_active?: boolean;
  status?: "ACTIVE" | "INACTIVE";
  // Free-form descriptive context (terms & conditions, description, etc.).
  metadata?: Record<string, any> | null;
}

export async function getManagedEvents(): Promise<ManagedEvent[]> {
  const r = await axiosInstance.get("/managed-event");
  return r.data.managedEvents ?? [];
}
export async function getManagedEventsBy(params: {
  product_id?: string;
  region_id?: string;
  team_id?: string;
  status?: string;
}): Promise<ManagedEvent[]> {
  const r = await axiosInstance.get("/managed-event", { params });
  return r.data.managedEvents ?? [];
}
// Managed events derived from a product's bundle session_types (what its
// sessions actually book) rather than the Team↔Product join.
export async function getManagedEventsByBundles(params: {
  product_id?: string;
  region_id?: string;
}): Promise<ManagedEvent[]> {
  const r = await axiosInstance.get("/managed-event/by-bundles", { params });
  return r.data.managedEvents ?? [];
}
export async function getManagedEvent(id: string): Promise<ManagedEvent> {
  const r = await axiosInstance.get(`/managed-event/${id}`);
  return r.data;
}
export async function createManagedEvent(data: ManagedEventInput) {
  return (await axiosInstance.post("/managed-event", data)).data;
}
export async function updateManagedEvent(
  id: string,
  data: Partial<ManagedEventInput>,
) {
  return (await axiosInstance.patch(`/managed-event/${id}`, data)).data;
}
export async function deleteManagedEvent(id: string) {
  return (await axiosInstance.delete(`/managed-event/${id}`)).data;
}

export type SetupManagedEventPayload = ManagedEventInput;
export interface SetupManagedEventResult {
  managedEvent: ManagedEvent;
  created: { individual: number };
  unmatched: { id: string; name: string; reason: string }[];
}
export async function setupManagedEvent(
  payload: SetupManagedEventPayload,
): Promise<SetupManagedEventResult> {
  return (await axiosInstance.post("/managed-event/setup", payload)).data;
}

export interface PreviewEventTypesResult {
  totalProfessionals: number;
  matched: {
    professional: { id: string; name: string };
    scheduling_url: string | null;
  }[];
  unmatched: { id: string; name: string; reason: string }[];
}
export async function previewManagedEventTypes(params: {
  team_ids: string[];
  name: string;
}): Promise<PreviewEventTypesResult> {
  const r = await axiosInstance.get("/managed-event/preview-event-types", {
    params: { team_ids: params.team_ids.join(","), name: params.name },
  });
  return r.data;
}

export interface GenerateEventTypesResult {
  created: number;
  skipped: number;
  totalProfessionals: number;
  matched: number;
  unmatched: { id: string; name: string; reason: string }[];
}
export async function generateManagedEventTypes(
  id: string,
): Promise<GenerateEventTypesResult> {
  return (await axiosInstance.post(`/managed-event/${id}/generate-event-types`))
    .data;
}
